import React from "react";
import { ArrowRight, BookOpen, Calculator, Compass, FileText, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import InlineCta from "@/components/common/InlineCta";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
const resourcesCopy = {
  eyebrow: {
    en: "Engineering Knowledge Base",
    zh: "工程知识库",
    es: "Base de conocimientos de ingeniería",
    de: "Technische Wissensdatenbank",
    fr: "Base de connaissances d'ingénierie",
    pt: "Base de conhecimento de engenharia"
  },
  h2: {
    en: "Technical documentation and guides",
    zh: "技术文档与指南",
    es: "Documentación técnica y guías",
    de: "Technische Dokumentation und Leitfäden",
    fr: "Documentation technique et guides",
    pt: "Documentação técnica e guias"
  },
  lead: {
    en: "Access mesh specifications, calculation models, and reference charts to support your filtration engineering and procurement requirements.",
    zh: "获取丝网规格参数、计算模型及参考图表，全力支持您的过滤工程设计与采购需求。",
    es: "Acceda a especificaciones de malla, modelos de cálculo y tablas de referencia para respaldar sus requisitos de ingeniería de filtración y adquisiciones.",
    de: "Greifen Sie auf Gewebespezifikationen, Berechnungsmodelle und Referenztabellen zu, um Ihre Filtrations- und Beschaffungsanforderungen zu unterstützen.",
    fr: "Accédez aux spécifications de toiles, modèles de calcul et tableaux de référence pour soutenir vos exigences d'ingénierie de filtration et d'approvisionnement.",
    pt: "Acesse especificações de malha, modelos de cálculo e tabelas de referência para apoiar seus requisitos de engenharia de filtração e compras."
  },
  explorePrefix: {
    en: "Explore",
    zh: "查看",
    es: "Explorar",
    de: "Erkunden Sie",
    fr: "Explorer",
    pt: "Explorar"
  },
  stripTitle: {
    en: "Need customized specifications or test certifications?",
    zh: "需要定制规格或检测认证？",
    es: "¿Necesita especificaciones personalizadas o certificados de ensayo?",
    de: "Benötigen Sie kundenspezifische Spezifikationen oder Prüfzeugnisse?",
    fr: "Besoin de spécifications sur mesure ou de certificats d'essai ?",
    pt: "Precisa de especificações personalizadas ou certificados de teste?"
  },
  stripDesc: {
    en: "Our technical engineering team provides custom weave design, differential pressure modeling, and material verification.",
    zh: "我们的技术工程团队可提供定制编织设计、差压流体建模以及材料检测验证服务。",
    es: "Nuestro equipo de ingeniería técnica ofrece diseño de tejido a medida, modelado de presión diferencial y verificación de materiales.",
    de: "Unser technisches Ingenieurteam bietet kundenspezifisches Gewebedesign, Differenzdruckmodellierung und Materialverifizierung.",
    fr: "Notre équipe d'ingénierie technique propose la conception de tissages sur mesure, la modélisation de perte de charge et la vérification des matériaux.",
    pt: "Nossa equipe de engenharia técnica oferece projeto de tecelagem personalizado, modelagem de pressão diferencial e verificação de materiais."
  },
  stripBtn: {
    en: "Request engineering quote",
    zh: "索取工程报价",
    es: "Solicitar cotización de ingeniería",
    de: "Technisches Angebot anfordern",
    fr: "Demander un devis d'ingénierie",
    pt: "Solicitar cotação de engenharia"
  }
};
interface ResourceItem {
  title: {
    en: string;
    zh: string;
    es: string;
    de: string;
    fr: string;
    pt: string;
  };
  description: {
    en: string;
    zh: string;
    es: string;
    de: string;
    fr: string;
    pt: string;
  };
  to: string;
  category: {
    en: string;
    zh: string;
    es: string;
    de: string;
    fr: string;
    pt: string;
  };
  icon: React.ComponentType<{
    className?: string;
    "aria-hidden"?: boolean | "true" | "false";
  }>;
}
const resources: ResourceItem[] = [{
  title: {
    en: "Datasheets",
    zh: "数据规格表",
    es: "Fichas técnicas",
    de: "Datenblätter",
    fr: "Fiches techniques",
    pt: "Fichas técnicas"
  },
  description: {
    en: "Aperture tolerances, wire diameters, and alloy chemical compositions across weave patterns.",
    zh: "涵盖各类编织模式的孔径公差、丝径参数及合金化学成分。",
    es: "Tolerancias de apertura, diámetros de alambre y composiciones químicas de aleaciones en distintos patrones de tejido.",
    de: "Maschentoleranzen, Drahtdurchmesser und chemische Legierungszusammensetzungen über verschiedene Webarten.",
    fr: "Tolérances d'ouverture, diamètres de fil et compositions chimiques des alliages selon les armures.",
    pt: "Tolerâncias de abertura, diâmetros de arame e composições químicas de ligas em diferentes padrões de tecelagem."
  },
  to: "/DatasheetsPage",
  category: {
    en: "Specifications",
    zh: "规格标准",
    es: "Especificaciones",
    de: "Spezifikationen",
    fr: "Spécifications",
    pt: "Especificações"
  },
  icon: FileText
}, {
  title: {
    en: "Selection guides",
    zh: "选型指南",
    es: "Guías de selección",
    de: "Auswahlleitfäden",
    fr: "Guides de sélection",
    pt: "Guias de seleção"
  },
  description: {
    en: "Compare weave geometries, micron retention ratings, and mechanical strength for your application.",
    zh: "对比编织几何结构、微米级拦截精度及机械强度，匹配精准应用场景。",
    es: "Compare geometrías de tejido, clasificaciones de retención en micras y resistencia mecánica para su aplicación.",
    de: "Vergleichen Sie Webgeometrien, Mikron-Rückhalteraten und mechanische Festigkeit für Ihre Anwendung.",
    fr: "Comparez les géométries de tissage, les seuils de rétention en microns et la résistance mécanique pour votre application.",
    pt: "Compare geometrias de tecelagem, retenção em mícrons e resistência mecânica para sua aplicação."
  },
  to: "/ProductSelectionPage",
  category: {
    en: "Engineering",
    zh: "工程选型",
    es: "Ingeniería",
    de: "Ingenieurwesen",
    fr: "Ingénierie",
    pt: "Engenharia"
  },
  icon: Compass
}, {
  title: {
    en: "Product catalog",
    zh: "产品目录",
    es: "Catálogo de productos",
    de: "Produktkatalog",
    fr: "Catalogue produits",
    pt: "Catálogo de produtos"
  },
  description: {
    en: "Comprehensive inventory of woven rolls, sintered laminates, discs, and fabricated filter components.",
    zh: "涵盖编织网卷、烧结层压网、滤片及深加工过滤组件的完整产品清单。",
    es: "Inventario integral de rollos tejidos, laminados sinterizados, discos y componentes de filtro fabricados.",
    de: "Umfassendes Sortiment an Geweberollen, Sinterlaminaten, Ronden und konfektionierten Filterkomponenten.",
    fr: "Inventaire complet de rouleaux tissés, laminés frittés, disques et composants filtrants confectionnés.",
    pt: "Inventário abrangente de rolos tecidos, laminados sinterizados, discos e componentes de filtro fabricados."
  },
  to: "/ProductCatalogPage",
  category: {
    en: "Catalog",
    zh: "产品总览",
    es: "Catálogo",
    de: "Katalog",
    fr: "Catalogue",
    pt: "Catálogo"
  },
  icon: BookOpen
}, {
  title: {
    en: "Calculation guides",
    zh: "计算与评估指南",
    es: "Guías de cálculo",
    de: "Berechnungsleitfäden",
    fr: "Guides de calcul",
    pt: "Guias de cálculo"
  },
  description: {
    en: "Formulas and step-by-step models for open area percentage, flow rate, and pressure drop estimations.",
    zh: "开孔率计算公式、流量估算及压降 (Delta P) 逐步评估模型。",
    es: "Fórmulas y modelos paso a paso para porcentaje de área abierta, caudal y estimaciones de caída de presión.",
    de: "Formeln und schrittweise Modelle für den Prozentsatz offener Siebflächen, Durchflussraten und Druckverlustabschätzungen.",
    fr: "Formules et modèles étape par étape pour le pourcentage de surface ouverte, le débit et les estimations de perte de charge.",
    pt: "Fórmulas e modelos passo a passo para porcentagem de área aberta, vazão e estimativas de queda de pressão."
  },
  to: "/CalculationGuidesPage",
  category: {
    en: "Calculations",
    zh: "工程计算",
    es: "Cálculos",
    de: "Berechnungen",
    fr: "Calculs",
    pt: "Cálculos"
  },
  icon: Calculator
}, {
  title: {
    en: "Technical charts",
    zh: "技术对照表",
    es: "Tablas técnicas",
    de: "Technische Tabellen",
    fr: "Tableaux techniques",
    pt: "Tabelas técnicas"
  },
  description: {
    en: "Reference conversion matrices for mesh counts, micron ratings, wire gauges, and roll weights.",
    zh: "目数、微米精度、线规标号与网卷重量的实用换算对照表。",
    es: "Matrices de conversión de referencia para número de malla, micras, calibres de alambre y pesos de rollo.",
    de: "Referenz-Umrechnungsmatrizen für Maschenzahlen, Mikron-Werte, Drahtstärken und Rollengewichte.",
    fr: "Matrices de conversion de référence pour le nombre de mailles, le seuil en microns, le calibre du fil et le poids des rouleaux.",
    pt: "Matrizes de conversão de referência para contagem de malha, classificação em mícrons, bitolas de arame e pesos de rolo."
  },
  to: "/TechnicalChartsPage",
  category: {
    en: "Reference",
    zh: "参考矩阵",
    es: "Referencia",
    de: "Referenz",
    fr: "Référence",
    pt: "Referência"
  },
  icon: SlidersHorizontal
}];
export default function Resources() {
  const {
    lang
  } = useLanguage();
  return <section id="technicalresourcegrid" data-nav="light" className="w-full bg-muted/50 mf-section border-b border-border">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal>
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="outline" className="mb-3 border-border bg-card font-mono mf-caption text-foreground">
              {pick(lang, resourcesCopy.eyebrow)}
            </Badge>
            <h2 className="mf-h2 tracking-tight text-foreground font-default">
              {pick(lang, resourcesCopy.h2)}
            </h2>
            <p className="mt-3 mf-lead text-muted-foreground">
              {pick(lang, resourcesCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Resources Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {resources.map((item, index) => {
          const Icon = item.icon;
          const isFeatured = index === 0;
          const titleStr = pick(lang, item.title);
          const descStr = pick(lang, item.description);
          const catStr = pick(lang, item.category);
          const exploreLabel = lang === "zh" ? `查看${titleStr}` : `${pick(lang, resourcesCopy.explorePrefix)} ${titleStr.toLowerCase()}`;
          return <Reveal key={item.to} delay={index * 60} className={isFeatured ? "sm:col-span-2 lg:col-span-2 xl:col-span-2" : ""}>
                <Card className="group flex h-full flex-col justify-between border-border bg-card rounded-lg transition-[border-color] duration-200 hover:border-primary/40">
                  <div>
                    <CardHeader className="space-y-3 pb-3">
                      <div className="flex items-center justify-between">
                        <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                          <Icon className="size-5" aria-hidden="true" />
                        </div>
                        <Badge variant="outline" className="bg-muted mf-caption font-mono text-muted-foreground border-border">
                          {catStr}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-semibold text-card-foreground group-hover:text-primary">
                        <Link to={item.to} className="focus:outline-hidden">
                          {titleStr}
                        </Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="mf-caption leading-relaxed text-muted-foreground">
                        {descStr}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2">
                    <Button variant="ghost" size="sm" asChild className="group/btn w-full justify-between px-0 font-medium text-foreground hover:bg-transparent hover:text-primary">
                      <Link to={item.to}>
                        <span>{exploreLabel}</span>
                        <ArrowRight className="size-4" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Quick Assistance Strip - Single Mid-Page Quote Prompt */}
        <InlineCta text={`${pick(lang, resourcesCopy.stripTitle)} ${pick(lang, resourcesCopy.stripDesc)}`} buttonLabel={pick(lang, resourcesCopy.stripBtn)} to="/RequestAQuotePage" />
      </div>
    </section>;
}